import {
  type Basket,
  type BasketItem,
  type Category,
  type DeliveryAddress,
  type Location,
  type RewardItem,
  type SubmitBasketData,
  type OrderDetails,
} from '@koala/sdk';
import { type PRODUCT_LOCATION_LABELS } from '@/constants/checkout';
import { type IBasketFulfillment, type ServerBasketFulfillment } from '@/types/fulfillment';
import { type IBasketState } from '@/types/basket';

const basketActions = {
  INIT_NEW_BASKET: '/koala/basket/INIT_NEW_BASKET',
  INIT_SESSION_STORAGE_BASKET: '/koala/basket/INIT_SESSION_STORAGE_BASKET',
  BASKET_ITEMS_SYNCED_WITH_STORE: '/koala/basket/BASKET_ITEMS_SYNCED_WITH_STORE',
  HYDRATE_BASKET: '/koala/basket/HYDRATE_BASKET',
  CLEAR_BASKET: '/koala/basket/CLEAR_BASKET',
  DEPERSONALIZE_BASKET: '/koala/basket/DEPERSONALIZE_BASKET',
  REMOVE_ITEM: '/koala/basket/REMOVE_ITEM',
  ADD_ITEM: '/koala/basket/ADD_ITEM',
  ADD_ITEM_TOAST: '/koala/basket/ADD_ITEM_TOAST',
  REPLACE_BASKET: '/koala/basket/REPLACE_BASKET',
  SUBMIT_CHECKOUT: '/koala/basket/SUBMIT_CHECKOUT',
  SUBMIT_CHECKOUT_TRIGGERED: '/koala/basket/SUBMIT_CHECKOUT_TRIGGERED',
  ADD_REWARDS: '/koala/basket/ADD_REWARDS',
  REWARDS_SUCCESS: '/koala/basket/REWARDS_SUCCESS',
  REMOVE_REWARD: '/koala/basket/REMOVE_REWARD',
  PROMO_SUCCESS: '/koala/basket/PROMO_SUCCESS',
  REMOVE_PROMO: '/koala/basket/REMOVE_PROMO',
  REWARDS: '/koala/basket/REWARDS',
  BASKET_SUCCESS: '/koala/basket/BASKET_SUCCESS',
  BASKET_LOADING: '/koala/basket/BASKET_LOADING',
  COMPLETED: '/koala/basket/COMPLETED',
  ORDER_CONFIRMATION: '/koala/basket/ORDER_CONFIRMATION',
  VALIDATE_BASKET: '/koala/basket/VALIDATE_BASKET',
  ADD_PICKUP_TIME: '/koala/basket/ADD_PICKUP_TIME',
  REMOVE_PICKUP_TIME: '/koala/basket/REMOVE_PICKUP_TIME',
  REHYDRATE_LOCAL_STORAGE_LOCATION: '/koala/basket/REHYDRATE_LOCAL_STORAGE_LOCATION',
  UPDATE_BASKET_FULFILLMENT: '/koala/basket/UPDATE_BASKET_FULFILLMENT',
  REVALIDATE_BASKET_ITEMS: '/koala/basket/REVALIDATE_BASKET_ITEMS',
  REOPEN_BASKET_ITEM_FOR_OPTIONS: '/koala/basket/REOPEN_BASKET_ITEM_FOR_OPTIONS',
  REVALIDATE_BASKET_ITEMS_SUCCESS: '/koala/basket/REVALIDATE_BASKET_ITEMS_SUCCESS',
  DESTROY_BASKET: '/koala/basket/DESTROY_BASKET',
  REORDER_BASKET: '/koala/basket/REORDER_BASKET',
  REORDER_BASKET_SUBMIT: '/koala/basket/REORDER_BASKET_SUBMIT',
  REORDER_BASKET_SUCCESS: '/koala/basket/REORDER_BASKET_SUCCESS',
  REORDER_BASKET_FAILURE: '/koala/basket/REORDER_BASKET_FAILURE',
  REORDER_BASKET_REVIEW_MISSING_ITEMS: 'REORDER_BASKET_REVIEW_MISSING_ITEMS',
  REORDER_BASKET_CONFIRM_MISSING_ITEMS: 'REORDER_BASKET_CONFIRM_MISSING_ITEMS',
  BASKET_ITEM_SUCCESS: '/koala/basket/BASKET_ITEM_SUCCESS',
  SET_UNAVAILABLE_BASKET_ITEMS: '/koala/basket/SET_UNAVAILABLE_BASKET_ITEMS',
  REMOVE_UNAVAILABLE_BASKET_ITEMS: '/koala/basket/REMOVE_UNAVAILABLE_BASKET_ITEMS',
  REMOVE_UNAVAILABLE_BASKET_ITEMS_SUCCESS: '/koala/basket/REMOVE_UNAVAILABLE_BASKET_ITEMS_SUCCESS',
  CLEAR_ASAP_ADJUSTMENT_REASON: '/koala/basket/CLEAR_ASAP_ADJUSTMENT_REASON',
  CLEAR_CHECKOUT_WANTED_AT: '/koala/basket/CLEAR_CHECKOUT_WANTED_AT',

  basketItemsSyncedWithStore: (items: BasketItem[]) => ({
    payload: items,
    type: basketActions.BASKET_ITEMS_SYNCED_WITH_STORE,
  }),
  initBasket: (
    location: Location,
    menuCategories: Category[],
    basketItems?: BasketItem[],
    address?: DeliveryAddress,
    time_wanted?: string,
  ) => ({
    type: basketActions.INIT_NEW_BASKET,
    location,
    menuCategories,
    basketItems,
    address,
    time_wanted,
  }),
  initializeSessionStorageBasket: () => ({
    type: basketActions.INIT_SESSION_STORAGE_BASKET,
  }),
  clearBasket: () => ({
    type: basketActions.CLEAR_BASKET,
  }),
  depersonalizeBasket: () => ({
    type: basketActions.DEPERSONALIZE_BASKET,
  }),
  hydrateBasket: () => ({
    type: basketActions.HYDRATE_BASKET,
  }),
  removeItem: (index: number, item: BasketItem) => ({
    type: basketActions.REMOVE_ITEM,
    index,
    item,
  }),
  addItem: (item: BasketItem, label: PRODUCT_LOCATION_LABELS, index?: number | null) => ({
    type: basketActions.ADD_ITEM,
    item,
    label,
    index,
  }),
  addItemToast: (item: BasketItem, index?: number) => ({
    type: basketActions.ADD_ITEM_TOAST,
    item,
    index,
  }),
  replaceBasket: (
    basketItems: BasketItem[],
    location: Location,
    menuCategories: Category[],
    createdAt: string,
    address?: DeliveryAddress,
  ) => ({
    type: basketActions.REPLACE_BASKET,
    basketItems,
    location,
    menuCategories,
    createdAt,
    address,
  }),
  addRewards: (basketId: string, rewardCodes: string[], locationId: number) => ({
    type: basketActions.ADD_REWARDS,
    basketId,
    rewardCodes,
    locationId,
  }),
  returnAvailableRewards: (rewards: RewardItem[]) => ({
    // the available rewards returned from OLO
    type: basketActions.REWARDS,
    rewards,
  }),
  setAppliedRewardCodes: (appliedRewards: string[]) => ({
    // the rewards codes applied to the basket
    type: basketActions.REWARDS_SUCCESS,
    appliedRewards,
  }),
  removeReward: (
    basketId: string,
    rewardCode: string,
    appliedRewards: string[],
    locationId: number,
  ) => ({
    type: basketActions.REMOVE_REWARD,
    basketId,
    rewardCode,
    appliedRewards,
    locationId,
  }),
  setAppliedPromoCode: (appliedPromo: string) => ({
    type: basketActions.PROMO_SUCCESS,
    appliedPromo,
  }),
  removePromo: (basketId: string, promoCode: string, locationId: number) => ({
    type: basketActions.REMOVE_PROMO,
    basketId,
    promoCode,
    locationId,
  }),
  submitCheckoutTriggered: (orderSubmitting: boolean) => ({
    type: basketActions.SUBMIT_CHECKOUT_TRIGGERED,
    orderSubmitting,
  }),
  submitCheckout: (basketId: string, locationId: number, orderData: SubmitBasketData) => ({
    type: basketActions.SUBMIT_CHECKOUT,
    basketId,
    locationId,
    orderData,
  }),
  success: (checkoutBasket: Basket) => ({
    type: basketActions.BASKET_SUCCESS,
    checkoutBasket,
  }),
  loading: (loading: boolean) => ({
    type: basketActions.BASKET_LOADING,
    loading,
  }),
  completed: () => ({
    type: basketActions.COMPLETED,
  }),
  confirmation: () => ({
    type: basketActions.ORDER_CONFIRMATION,
  }),
  setPickupTime: (basketId: string, locationId: number, wantedAt: string) => ({
    type: basketActions.ADD_PICKUP_TIME,
    basketId,
    locationId,
    wantedAt,
  }),
  removePickupTime: (basketId: string, locationId: number) => ({
    type: basketActions.REMOVE_PICKUP_TIME,
    basketId,
    locationId,
  }),
  rehydrateLocalStorageLocation: (location: Location) => ({
    type: basketActions.REHYDRATE_LOCAL_STORAGE_LOCATION,
    location,
  }),
  updateBasketFulfillment: (fulfillment: IBasketFulfillment | ServerBasketFulfillment) => ({
    type: basketActions.UPDATE_BASKET_FULFILLMENT,
    fulfillment,
  }),
  revalidateBasketItems: (menuCategories: Category[]) => ({
    type: basketActions.REVALIDATE_BASKET_ITEMS,
    menuCategories,
  }),
  revalidateBasketItemsSuccess: (basket: IBasketState) => ({
    type: basketActions.REVALIDATE_BASKET_ITEMS_SUCCESS,
    basket,
  }),
  /**
   * Reopen one cart line's customizer so the diner can re-choose a required option — TEAM-15138.
   *
   * `index` is a position in `basket.content.basket_items`, which is what
   * `customizeActions.setProduct` indexes and what coo-api's `basket_item_index` names.
   *
   * An action rather than three lines at each call site because three unrelated surfaces need it —
   * the cart-revalidation toast, the checkout reconciliation modal and the add-item rejection
   * toast — and the lookup it needs (`state.app.menu.basketMenu`) is not reachable from the toast
   * component. See `reopenBasketItemForOptionsSaga`.
   */
  reopenBasketItemForOptions: (
    index: number,
    options: {
      /**
       * Set only by the checkout reconciliation modal. Basket creation failed there, so the modal
       * is dismissed to make room for the customizer and the diner is left on `/checkout` with no
       * control that re-enters initialization — every `initializeOrder` dispatch site is either
       * inside that modal or on the sidecart's Checkout button, a page away. The saga re-enters it
       * for them once the item is fixed. Off elsewhere: a cart-page reopen has no order to resume.
       */
      resumeCheckout?: boolean;
      /**
       * What the line looked like when the affordance was raised. See `IGlobalToastReopen` for why
       * a toast-borne index needs one; a dispatch from a blocking modal does not.
       */
      expectedSignature?: string;
    } = {},
  ) => ({
    type: basketActions.REOPEN_BASKET_ITEM_FOR_OPTIONS,
    index,
    resumeCheckout: options.resumeCheckout ?? false,
    expectedSignature: options.expectedSignature,
  }),
  destroyBasket: () => ({
    type: basketActions.DESTROY_BASKET,
  }),
  reorderBasket: (
    order: OrderDetails,
    label: string,
    routeToCheckout = false,
    skipUnavailable = false,
  ) => ({
    type: basketActions.REORDER_BASKET,
    order,
    label,
    routeToCheckout,
    skipUnavailable,
  }),
  reorderBasketSubmit: (
    order: OrderDetails,
    label: string,
    address?: DeliveryAddress,
    routeToCheckout = false,
    skipUnavailable = false,
  ) => ({
    type: basketActions.REORDER_BASKET_SUBMIT,
    order,
    label,
    address,
    routeToCheckout,
    skipUnavailable,
  }),
  reorderBasketSuccess: (order: OrderDetails, routeToCheckout = false) => ({
    type: basketActions.REORDER_BASKET_SUCCESS,
    order,
    routeToCheckout,
  }),
  reorderBasketReviewMissingItems: () => ({
    type: basketActions.REORDER_BASKET_REVIEW_MISSING_ITEMS,
  }),
  basketItemSuccess: (value: boolean) => ({
    type: basketActions.BASKET_ITEM_SUCCESS,
    value,
  }),
  setUnavailableBasketItems: (basketItemIds: string[], errorCode?: string | null) => ({
    type: basketActions.SET_UNAVAILABLE_BASKET_ITEMS,
    basketItemIds,
    errorCode,
  }),
  removeUnavailableBasketItems: (options?: { redirectTo?: string }) => ({
    type: basketActions.REMOVE_UNAVAILABLE_BASKET_ITEMS,
    redirectTo: options?.redirectTo,
  }),
  removeUnavailableBasketItemsSuccess: (removedIds: string[]) => ({
    type: basketActions.REMOVE_UNAVAILABLE_BASKET_ITEMS_SUCCESS,
    removedIds,
  }),
  clearAsapAdjustmentReason: () => ({
    type: basketActions.CLEAR_ASAP_ADJUSTMENT_REASON,
  }),
  clearCheckoutWantedAt: () => ({
    type: basketActions.CLEAR_CHECKOUT_WANTED_AT,
  }),
};

export default basketActions;
